import React, { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Row, Col, Form, Button } from 'react-bootstrap';
import { DraggableModalDialog } from './DraggableModalDialog';
import DataTable from '../DataTable/datatable.component';
import DateColumn from '../DataTable/datatable.datecolumn';
import NoFilter from '../DataTable/datatable.nofilter';
import { LabResult } from '../Types/LabResult';
import CommentDialog from '../Dialogs/CommentDialog';

interface SamplesTableProps {
    samples: LabResult[];
    removeEntry: (labResult: LabResult, includeList: boolean) => void;
    includeList: boolean;
}

function SamplesTable({ samples, removeEntry, includeList }: SamplesTableProps) {

    const { t } = useTranslation();

    const tableColumns = [
        {
            Header: t('LabDetails.Samples.SampleId'),
            accessor: 'smSampleId'
        },
        DateColumn({ header: t('LabDetails.Samples.SampleTime'), accessor: 'sampleTimeUtc' }),
        {
            Header: t('LabDetails.Samples.SamplePoint'),
            accessor: 'samplingPoint'
        },
        {
            Header: 'Action',
            accessor: '',
            Filter: NoFilter,
            Cell: (props: any) => {

                let actionName = "Exclude";
                if (includeList && props.row.original.isOverride) {
                    actionName = "Remove";
                }
                else if (!includeList) {
                    actionName = "Include";
                }

                return <Button onClick={() => removeEntry(props.row.original, includeList)}>{actionName}</Button>;
            }
        }
    ];

    const initialState = {
        sortBy: [
            {
                id: 'sampleTimeUtc',
                desc: false
            },
            {
                id: 'sampleID',
                desc: false
            }
        ],
        pageSize: 100
    };

    return <DataTable tableColumns={tableColumns} tableData={samples} initialState={initialState} showPaging={false} />;
}

export interface ManageLabResultsDialogProps {
    showDialog: boolean,
    closeDialog: () => void,
    onSave: (comment: string, includeSampleIds: string[], excludeSampleIds: string[]) => void,
    labResults: LabResult[],
    includeSampleIds: string[],
    excludeSampleIds: string[]
}

function ManageLabResultsDialog({ showDialog, closeDialog, onSave, labResults, includeSampleIds, excludeSampleIds }: ManageLabResultsDialogProps) {

    const { t } = useTranslation();
    const [showCommentDialog, setShowCommentDialog] = useState(false);

    const filterDistinctSamples = (value: LabResult, index: number, self: LabResult[]) => {
        return (
            self.findIndex(
                // Modify this function as you desire. You may want to calculate uniqueness depending only on specific fields, not all
                (obj) => obj.smSampleId === value.smSampleId
            ) === index
        );
    };

    const labResultsDictionary: { [key: string]: LabResult } = {};

    for (const labResult of labResults) {
        labResultsDictionary[labResult.smSampleId as string] = labResult;
    }

    let initialIncludedSamples = labResults.filter(filterDistinctSamples);

    for (const sampleId of includeSampleIds) {
        if (labResultsDictionary[sampleId]) {
            labResultsDictionary[sampleId].isOverride = true;
        } else {
            initialIncludedSamples.push({ smSampleId: sampleId, isOverride: true } as LabResult);
        }
    }

    const [includeSamples, setIncludeSamples] = useState(initialIncludedSamples);

    // Augment the sample entries with any details we might have
    let initialExcludedSamples = excludeSampleIds.map((sampleId: string) => ({ smSampleId: sampleId, isOverride: true, sampleTimeUtc: labResultsDictionary[sampleId]?.sampleTimeUtc, samplingPoint: labResultsDictionary[sampleId]?.samplingPoint } as LabResult));

    const [excludedSamples, setExcludedSamples] = useState(initialExcludedSamples);

    const removeEntry = (labResult: LabResult, includeList: boolean) => {
        if (includeList) {
            // removing from the include list
            setIncludeSamples(includeSamples.filter(s => s.smSampleId !== labResult.smSampleId));

            if (!labResult.isOverride) {
                // was NOT a manually added sample into the include list, so we need to exclude it
                let newExclusions = excludedSamples;
                newExclusions.push(labResult);
                setExcludedSamples(newExclusions);
            }
        } else {
            // remove from the exclude list
            setExcludedSamples(excludedSamples.filter(s => s.smSampleId !== labResult.smSampleId));

            if (!labResult.isOverride) {
                // Came from the set of lab results that were used
                let newInclusions = includeSamples;
                newInclusions.push(labResult);
                setIncludeSamples(newInclusions);
            }
        }
    };

    const addEntry = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (newSampleId !== "") {
            let newInclusions = includeSamples;
            newInclusions.push({ smSampleId: newSampleId, isOverride: true } as LabResult);
            setIncludeSamples([...newInclusions]);
        }

        setNewSampleId("");
    };

    const handleSaveWithComment = (comment: string) => {
        let includeSampleIds = includeSamples.filter(s => { return s.isOverride }).map(s => s.smSampleId as string);
        let excludeSampleIds = excludedSamples.map(s => s.smSampleId as string);

        onSave(comment, includeSampleIds, excludeSampleIds);
    };

    const [newSampleId, setNewSampleId] = useState("");

    return (
        <Modal dialogAs={DraggableModalDialog} className="detail-dialog" show={showDialog} onHide={closeDialog} backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>Manage Lab Results</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h4>Included Samples</h4>
                <SamplesTable samples={includeSamples} removeEntry={removeEntry} includeList={true} />
                <Form onSubmit={addEntry}>
                    <Form.Group as={Row}>
                        <Col sm="3">
                            <Form.Control name="newSampleId" type="text" placeholder="Sample ID" value={newSampleId} onChange={(e) => setNewSampleId(e.target.value)} />
                        </Col>
                        <Col sm="2">
                            <Button type="submit" disabled={!newSampleId}>Add</Button>
                        </Col>
                    </Form.Group>
                </Form>
                <hr></hr>
                <h4>Excluded Samples</h4>
                <SamplesTable samples={excludedSamples} removeEntry={removeEntry} includeList={false} />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShowCommentDialog(true)}>
                    <>{t("Application.Controls.Save")}</>
                </Button>
                <Button variant="secondary" onClick={closeDialog}>
                    <>{t("Application.Controls.Close")}</>
                </Button>
            </Modal.Footer>
            <CommentDialog showDialog={showCommentDialog} closeDialog={() => setShowCommentDialog(false)} onSave={handleSaveWithComment} />
        </Modal>
    );
}

export default ManageLabResultsDialog;